/**
 * Intake panel on a quote record (SA-401 D30/D31/D33/D37).
 *
 * View: the agenda's fields with values and an extracted/entered chip, plus
 * the Sources list (every paste kept on the record). Edit: the same typed
 * controls; with a running process the save is a revision and the panel says
 * so before Save (optional Reason). Paste → Extract → Proposals (checked only
 * where the current value is empty) → Accept copies into the form; Save runs
 * the ordinary write with the paste attached as `source`. Orders lane: plain
 * apiClient callbacks, no React Query (the 12 OrdersPage tests mount without
 * a provider). Self-gates on quote-class orders.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, CollapsibleSection, Input, Textarea } from '@/components/ui'
import { FieldBlock, type FieldBlockField } from '@/components/process/FieldBlock'
import { isBlank } from '@/components/process/fieldState'
import { extractedSource } from '@/components/process/fieldStateText'
import { FieldValue } from '@/components/process/FieldValue'
import { FactList, type Fact } from '@/components/records/FactList'
import { RecordSection } from '@/components/records/RecordSection'
import { intakeSummary } from '@/components/records/sectionSummaries'
import { birthSourceIndex, laterSourceKindWord, sourceKindWord } from '@/components/records/birthText'
import { APPLY_AS_REVISION, DISMISS, NOTHING_NEW, OPEN_IN_INBOX, refusalWords, revisionLanding, revisionNotice, updateBlockHeading } from '@/components/records/customerUpdateText'
import { UpdateProposal } from '@/components/records/UpdateProposal'
import { isIntakeLinkSlot } from '@/components/records/intakeSnapshot'
import { usePermissionStore } from '@/stores/permissionStore'
import { IntakeCompanyLink } from './IntakeCompanyLink'
import { isPlace, placeKey, placeWords } from '@/components/records/place'
import { isWindow, windowKey } from '@/components/records/window'
import { windowLine } from '@/components/records/windowText'
import type { CustomerUpdate, CustomerUpdateApplyPayload, IntakeExtraction, IntakeField, IntakeProvenance, IntakeRead, IntakeUpdatePayload } from '@/types/commerce'

/** The server's cap on a revision or dismissal reason (the request schemas' `max_length`). */
const REASON_MAX = 2000

type Values = Record<string, unknown>
type Provenance = Record<string, IntakeProvenance>

const asBlockField = (f: IntakeField): FieldBlockField =>
  ({ id: f.id, label: f.label, type: f.type, options: f.options ?? null, optional: !f.required, help: f.help ?? null,
     alternatives: f.alternatives })

function filled(v: unknown): boolean {
  return v !== null && v !== undefined && v !== ''
}
function same(a: unknown, b: unknown): boolean {
  // SA-1136: a window compares by its key, so a changed date or basis is a change.
  if (isWindow(a) || isWindow(b)) return windowKey(a) === windowKey(b)
  // SA-1051: a place compares by its key, so a changed code or choice is a change.
  if (isPlace(a) || isPlace(b)) return placeKey(a) === placeKey(b)
  return (filled(a) ? String(a) : '') === (filled(b) ? String(b) : '')
}
/** A value in words, never `[object Object]`: a window by its range (else its words), a place by its codes or text. */
const words = (v: unknown): string => (isWindow(v) ? windowLine(v) : isPlace(v) ? placeWords(v) : String(v))

interface Proposal { field: string; label: string; current: unknown; proposed: unknown; checked: boolean }

export function IntakePanel({
  orderId, orderType, outcome, personaId, getIntake, updateIntake, extractIntake, onIntakeChanged,
  chrome = 'collapsible', focused = false,
  getCustomerUpdates, extractCustomerUpdate, applyCustomerUpdate, dismissCustomerUpdate, refreshToken, defaultOpen = true,
  linkIntakeCompany,
}: {
  orderId: string
  orderType: string
  outcome: string | null
  personaId: string | null
  getIntake: (orderId: string) => Promise<IntakeRead>
  updateIntake: (orderId: string, payload: IntakeUpdatePayload) => Promise<IntakeRead>
  extractIntake: (agenda: string, text: string, personaId?: string) => Promise<IntakeExtraction>
  onIntakeChanged: () => void
  /** `collapsible` (default): today's `CollapsibleSection` form frame.
   * `section`: the record lane's one Intake section (SA-461, spec §7.4). */
  chrome?: 'collapsible' | 'section'
  /** `?panel=intake` (section chrome only). */
  focused?: boolean
  /** SA-1029 (spec §D23): the customer updates waiting on the record. Absent = no block. */
  getCustomerUpdates?: (orderId: string) => Promise<CustomerUpdate[]>
  extractCustomerUpdate?: (orderId: string, updateId: string) => Promise<CustomerUpdate>
  applyCustomerUpdate?: (orderId: string, updateId: string, payload: CustomerUpdateApplyPayload) => Promise<CustomerUpdate>
  dismissCustomerUpdate?: (orderId: string, updateId: string, reason?: string) => Promise<CustomerUpdate>
  /** Bumped by the page when something outside the panel moved the record (a bus event, a card write). */
  refreshToken?: number
  /** SA-1077 (Q1): the lane's default; the form frame and the Inbox sidebar pass nothing (today's open). */
  defaultOpen?: boolean
  /** SA-1456 (spec §7.5): link a company slot to a company, or clear it. Absent = no link rows (the
   * picker reads React Query, which only the record lane mounts). */
  linkIntakeCompany?: (orderId: string, slot: string, companyId: string | null) => Promise<IntakeRead>
}) {
  const [data, setData] = useState<IntakeRead | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState<Values>({})
  const [provenance, setProvenance] = useState<Provenance>({})
  const [loaded, setLoaded] = useState<Record<string, unknown>>({})
  const [reason, setReason] = useState('')
  const [pasteText, setPasteText] = useState('')
  const [pendingSource, setPendingSource] = useState<string | null>(null)
  const [proposals, setProposals] = useState<Proposal[] | null>(null)
  const [acceptedLine, setAcceptedLine] = useState<string | null>(null)
  const [extracting, setExtracting] = useState(false)
  const [extractError, setExtractError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const [updates, setUpdates] = useState<CustomerUpdate[]>([])
  const [applying, setApplying] = useState<string | null>(null)
  const [dismissing, setDismissing] = useState<string | null>(null)
  const [dismissReason, setDismissReason] = useState('')
  const [updateError, setUpdateError] = useState<string | null>(null)
  const [updateBusy, setUpdateBusy] = useState(false)
  const [accepting, setAccepting] = useState<string | null>(null)
  // Strict on the loaded store: the link PUT is `inbox:write`.
  const canLink = usePermissionStore((st) => st.isLoaded && st.permissions.has('inbox:write'))

  const loadUpdates = useCallback(async () => {
    // A failed updates read says so in the block; it never becomes a loadError.
    if (!getCustomerUpdates) return
    try {
      setUpdates(await getCustomerUpdates(orderId))
      setUpdateError(null)
    } catch (e) {
      setUpdateError((e as Error).message)
    }
  }, [getCustomerUpdates, orderId])

  const load = useCallback(async () => {
    try {
      const read = await getIntake(orderId)
      // SA-401 finding 2: a resolved-but-malformed response (e.g. a bare
      // Order fell through a test's/caller's generic fallback route) must
      // degrade through the same quiet loadError path, never reach render —
      // `data.fields.map(...)` on a non-array would unmount the whole page.
      if (!read || !Array.isArray(read.fields)) {
        setData(null)
        setLoadError('the response was not shaped like an intake record')
        return
      }
      setData(read)
      setLoadError(null)
    } catch (e) {
      setLoadError((e as Error).message)
      return
    }
    await loadUpdates()
  }, [getIntake, loadUpdates, orderId])

  useEffect(() => { if (orderType === 'quote') void load() }, [load, orderType])

  // Mid-edit, a moved token re-reads the updates list ONLY: re-reading the
  // intake would move `original` under the person's seeded values, and their
  // Save would send someone else's old values back as a revision. The
  // deferred intake read runs when they Cancel (a Save re-reads anyway).
  const editingRef = useRef(editing)
  editingRef.current = editing
  const deferredReload = useRef(false)
  const seenToken = useRef(refreshToken)
  useEffect(() => {
    if (refreshToken === undefined || refreshToken === seenToken.current) return
    seenToken.current = refreshToken
    if (orderType !== 'quote') return
    if (editingRef.current) {
      deferredReload.current = true
      void loadUpdates()
      return
    }
    void load()
  }, [refreshToken, load, loadUpdates, orderType])

  // SA-401 finding 3: mid-edit on one record, the panel must not carry that
  // draft over if it gets reused for another (OrdersPage also keys the
  // panel on order.id — belt and braces, the panel must be safe on its
  // own too). Resets on every orderId change, including the first mount.
  useEffect(() => {
    setEditing(false)
    setValues({})
    setProvenance({})
    setReason('')
    setPasteText('')
    setPendingSource(null)
    setProposals(null)
    setAcceptedLine(null)
    setExtractError(null)
    setSaveError(null)
    setSourcesOpen(false)
    setApplying(null)
    setDismissing(null)
    setDismissReason('')
    setUpdateError(null)
    setAccepting(null)
    setUpdates([])
  }, [orderId])

  const original = useMemo(() => Object.fromEntries((data?.fields ?? []).map((f) => [f.id, f.value])), [data])
  // SA-1047: a waiting response belongs to Quote versions; Intake lists change requests only.
  const waiting = updates.filter((u) => u.status === 'waiting' && u.kind !== 'response')
  const closed = outcome !== null || data?.process.status === 'closed'
  const running = data?.process.status === 'running'

  if (orderType !== 'quote') return null

  const startEdit = () => {
    // F2: seed from the field's own value, unchanged — an unset boolean
    // stays `null` (never coerced to `false`), so an untouched boolean
    // compares `same()` to itself and never rides Save as a spurious
    // change (D31: no trivial-edit bypass, but also no PHANTOM edit).
    setValues(Object.fromEntries((data?.fields ?? []).map((f) => [f.id, f.value])))
    setLoaded(Object.fromEntries((data?.fields ?? []).map((f) => [f.id, f.value])))
    setProvenance(Object.fromEntries((data?.fields ?? []).filter((f) => f.provenance).map((f) => [f.id, f.provenance as IntakeProvenance])))
    setReason(''); setPasteText(''); setPendingSource(null); setProposals(null); setAcceptedLine(null); setExtractError(null); setSaveError(null)
    setEditing(true)
  }

  const setField = (id: string, v: unknown) => {
    setValues((s) => ({ ...s, [id]: v }))
    setProvenance((p) => ({ ...p, [id]: 'entered' }))
  }

  const sourceOf = (id: string) => (data?.sources ?? []).find((s) => s.fields_set.includes(id))
  const extracted = (id: string) => {
    const s = sourceOf(id)
    return extractedSource(s?.kind ?? '', s?.pasted_at ?? null)
  }
  // SA-1196: a restored value is the loaded one again, with its loaded provenance.
  const restoreField = (f: IntakeField, v: unknown) => {
    setValues((s) => ({ ...s, [f.id]: v }))
    setProvenance((p) => {
      const next = { ...p }
      if (f.provenance) next[f.id] = f.provenance
      else delete next[f.id]
      return next
    })
  }

  const handleExtract = async () => {
    if (!data || !pasteText.trim()) return
    setExtracting(true); setExtractError(null); setProposals(null); setAcceptedLine(null)
    try {
      // F1: forward the record's own persona so the extraction call
      // resolves the RAG API key against the persona owner, not the
      // caller — a manager viewing another user's record must not 503.
      const result = await extractIntake(data.workflow_id, pasteText, personaId ?? undefined)
      const labels = Object.fromEntries(data.fields.map((f) => [f.id, f.label]))
      const rows: Proposal[] = Object.entries(result.values)
        .filter(([k, v]) => k in labels && !same(values[k], v))
        .map(([k, v]) => ({ field: k, label: labels[k], current: values[k], proposed: v, checked: !filled(values[k]) }))
      setPendingSource(pasteText)
      setProposals(rows)
      if (rows.length === 0) setAcceptedLine('Nothing new in this text — it will be kept as a source when you save.')
    } catch (e) {
      setExtractError((e as Error).message)
    } finally {
      setExtracting(false)
    }
  }

  const handleAccept = () => {
    if (!proposals) return
    const taken = proposals.filter((p) => p.checked)
    setValues((s) => ({ ...s, ...Object.fromEntries(taken.map((p) => [p.field, p.proposed])) }))
    setProvenance((p) => ({ ...p, ...Object.fromEntries(taken.map((t) => [t.field, 'extracted' as const])) }))
    setAcceptedLine(`${taken.length} accepted, ${proposals.length - taken.length} ignored`)
    setProposals(null)
  }

  const handleApplyUpdate = async (u: CustomerUpdate) => {
    if (!data || !extractCustomerUpdate) return
    setUpdateBusy(true); setUpdateError(null)
    try {
      const fresh = await extractCustomerUpdate(orderId, u.id)
      const current = Object.fromEntries(data.fields.map((f) => [f.id, f.value]))
      const labels = Object.fromEntries(data.fields.map((f) => [f.id, f.label]))
      const rows: Proposal[] = Object.entries(fresh.proposal?.values ?? {})
        .filter(([k, v]) => k in labels && !same(current[k], v))
        .map(([k, v]) => ({ field: k, label: labels[k], current: current[k], proposed: v, checked: !filled(current[k]) }))
      setValues(current)
      setLoaded(current)
      setProvenance(Object.fromEntries(data.fields.filter((f) => f.provenance).map((f) => [f.id, f.provenance as IntakeProvenance])))
      setReason(''); setPasteText(''); setPendingSource(null); setExtractError(null); setSaveError(null)
      setProposals(rows)
      setAcceptedLine(rows.length === 0 ? NOTHING_NEW : null)
      setApplying(u.id)
      setEditing(true)
    } catch (e) {
      setUpdateError((e as Error).message)
    } finally {
      setUpdateBusy(false)
    }
  }

  // SA-1042 (spec §D11): Accept sends the ticked rows through the same apply as A's Save.
  const handleAcceptUpdate = async (u: CustomerUpdate, payload: CustomerUpdateApplyPayload) => {
    if (!applyCustomerUpdate) return
    setUpdateBusy(true); setAccepting(u.id); setUpdateError(null)
    try {
      await applyCustomerUpdate(orderId, u.id, payload)
      await load()
      onIntakeChanged()
    } catch (e) {
      setUpdateError((e as Error).message)
    } finally {
      setUpdateBusy(false); setAccepting(null)
    }
  }

  // SA-1042: "Edit before applying" opens the edit form with every proposed value
  // in place (as if every row were ticked), marked extracted; no table, no second extraction.
  const handleEditUpdate = (u: CustomerUpdate) => {
    if (!data) return
    const proposed = Object.fromEntries((u.changes ?? []).map((c) => [c.field.id, c.proposed]))
    setLoaded(Object.fromEntries(data.fields.map((f) => [f.id, f.value])))
    setValues({ ...Object.fromEntries(data.fields.map((f) => [f.id, f.value])), ...proposed })
    setProvenance({
      ...Object.fromEntries(data.fields.filter((f) => f.provenance).map((f) => [f.id, f.provenance as IntakeProvenance])),
      ...Object.fromEntries(Object.keys(proposed).map((k) => [k, 'extracted' as const])),
    })
    setReason(''); setPasteText(''); setPendingSource(null); setExtractError(null); setSaveError(null)
    setProposals(null); setAcceptedLine(null)
    setApplying(u.id)
    setEditing(true)
  }

  const handleDismiss = async (u: CustomerUpdate) => {
    if (!dismissCustomerUpdate) return
    setUpdateBusy(true); setUpdateError(null)
    try {
      await dismissCustomerUpdate(orderId, u.id, dismissReason.trim() || undefined)
      setDismissing(null); setDismissReason('')
      await load()
      onIntakeChanged()
    } catch (e) {
      setUpdateError((e as Error).message)
    } finally {
      setUpdateBusy(false)
    }
  }

  const handleLink = async (slot: string, companyId: string | null) => {
    if (!linkIntakeCompany) return
    const read = await linkIntakeCompany(orderId, slot, companyId)
    if (read && Array.isArray(read.fields)) setData(read)
    onIntakeChanged()
  }

  // SA-1044: the fields the form changes, by handleSave's own compare; they name the step a save lands on.
  const changedIds = data ? data.fields.filter((f) => !same(original[f.id], values[f.id])).map((f) => f.id) : []

  const handleSave = async () => {
    if (!data) return
    const slots: Values = {}
    const prov: Provenance = {}
    for (const f of data.fields) {
      if (same(original[f.id], values[f.id])) continue
      slots[f.id] = filled(values[f.id]) ? values[f.id] : null
      if (filled(values[f.id])) prov[f.id] = provenance[f.id] ?? 'entered'
    }
    if (applying && applyCustomerUpdate) {
      setSaving(true); setSaveError(null)
      try {
        await applyCustomerUpdate(orderId, applying, { slots, provenance: prov, ...(reason.trim() ? { reason: reason.trim() } : {}) })
        setApplying(null)
        setEditing(false)
        await load()
        onIntakeChanged()
      } catch (e) {
        setSaveError((e as Error).message)
      } finally {
        setSaving(false)
      }
      return
    }
    const payload: IntakeUpdatePayload = {
      ...(Object.keys(slots).length ? { slots, provenance: prov } : {}),
      ...(pendingSource ? { source: { text: pendingSource } } : {}),
      ...(running && reason.trim() ? { reason: reason.trim() } : {}),
    }
    if (Object.keys(payload).length === 0) { setEditing(false); return }
    setSaving(true); setSaveError(null)
    try {
      setData(await updateIntake(orderId, payload))
      setEditing(false)
      onIntakeChanged()
    } catch (e) {
      setSaveError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  // SA-1456: a company slot's link, beside its row, where the caller can link and the server served one.
  const linkFor = (f: IntakeField) => {
    const link = linkIntakeCompany && isIntakeLinkSlot(f.id) ? data?.links?.[f.id] : undefined
    if (!link || !data) return null
    return (
      <IntakeCompanyLink key={`${f.id}:${typeof f.value === 'string' ? f.value : ''}`} label={f.label}
        text={typeof f.value === 'string' ? f.value : ''} link={link}
        canWrite={canLink && !closed} onLink={(companyId) => handleLink(f.id, companyId)} />
    )
  }

  const body = (
    <>
      {loadError && <p className="text-sm text-muted-foreground">Intake fields unavailable ({loadError}).</p>}
      {data && !editing && (
        <div className="space-y-3 text-sm">
          {updateError && <p className="text-sm text-destructive">{updateError}</p>}
          {waiting.map((u) => (
            <div key={u.id} data-testid="customer-update" className="space-y-2 rounded-lg border border-warning/40 bg-warning/5 p-2.5">
              <p className="text-xs text-muted-foreground">{updateBlockHeading(u)}</p>
              {u.excerpt && (
                <blockquote className="border-l-2 border-border pl-2">
                  <p className="line-clamp-4 whitespace-pre-wrap text-sm" title={u.excerpt}>{u.excerpt}</p>
                </blockquote>
              )}
              {u.canApply && u.changes && u.changes.length > 0 && applyCustomerUpdate && (
                <UpdateProposal
                  key={`${u.id}:${u.changes.map((c) => c.field.id).join(',')}`}
                  changes={u.changes} busy={updateBusy} accepting={accepting === u.id}
                  // SA-1044: an older server sends no map: no notice rather than a wrong one.
                  revision={running && data.process.revision_steps !== undefined ? { number: data.revision_count + 1, steps: data.process.revision_steps, current: data.process.current_step ?? null } : null}
                  onAccept={(payload) => void handleAcceptUpdate(u, payload)}
                  onEdit={() => handleEditUpdate(u)} />
              )}
              {u.canApply && u.changes && u.changes.length === 0 && (
                <p className="text-xs text-muted-foreground">{NOTHING_NEW}</p>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {u.canApply && u.changes == null && extractCustomerUpdate && (
                  <Button size="sm" disabled={updateBusy} onClick={() => void handleApplyUpdate(u)}>{updateBusy ? 'Extracting…' : APPLY_AS_REVISION}</Button>
                )}
                {!u.canApply && u.applyRefusal && <span className="text-xs text-muted-foreground">{refusalWords(u.applyRefusal)}</span>}
                {dismissCustomerUpdate && dismissing !== u.id && (
                  <Button size="sm" variant="ghost" disabled={updateBusy} onClick={() => { setDismissing(u.id); setDismissReason('') }}>{DISMISS}</Button>
                )}
                {u.conversationId && (
                  <Link to={`/inbox/${u.conversationId}${u.messageId ? `?message=${u.messageId}` : ''}`} className="text-xs text-primary underline-offset-2 hover:underline">{OPEN_IN_INBOX}</Link>
                )}
              </div>
              {dismissing === u.id && (
                <div className="flex flex-wrap items-end gap-2">
                  <Input id={`dismiss-${u.id}`} label="Reason for dismissing" value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} placeholder="optional" maxLength={REASON_MAX} />
                  <Button size="sm" disabled={updateBusy} onClick={() => void handleDismiss(u)}>Confirm dismiss</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDismissing(null)}>Keep</Button>
                </div>
              )}
            </div>
          ))}
          <FactList
            facts={data.fields.map((f): Fact => { const linked = linkFor(f); return {
              key: f.id,
              label: f.label,
              // `''` is empty here (the panel's own `filled`), and FieldValue
              // only dashes on null/undefined — map it, or the row goes blank.
              value: <FieldValue field={{ ...f, value: filled(f.value) ? f.value : null }} />,
              // A window's words ride WindowValue's Tip; a native title beside it would show two tooltips.
              // A company link's controls carry Tips of their own, so a row showing them takes no title either.
              title: filled(f.value) && !isWindow(f.value) && !(linked && canLink && !closed) ? words(f.value) : undefined,
              // SA-466 R1: mark the exception only. `entered` on every filled
              // row said nothing; the counts are in the section's closed line.
              after: (f.provenance === 'extracted' || linked) ? (
                <>
                  {f.provenance === 'extracted' && <span className="ml-1.5 rounded-full bg-info/10 px-1.5 text-[11px] text-info">extracted</span>}
                  {linked}
                </>
              ) : undefined,
            } })}
          />
          <div className="flex flex-wrap items-center gap-2">
            {!closed && <Button size="sm" variant="outline" onClick={startEdit}>Edit intake</Button>}
            <Button size="sm" variant="ghost" onClick={() => setSourcesOpen((o) => !o)}>Sources ({data.sources.length})</Button>
            {data.revision_count > 0 && <span className="text-xs text-muted-foreground">{data.revision_count} revision{data.revision_count === 1 ? '' : 's'}</span>}
          </div>
          {sourcesOpen && (
            <ol className="space-y-2">
              {data.sources.map((s, i) => (
                <li key={`${s.pasted_at}-${i}`} className="rounded-lg border border-border p-2">
                  <p
                    className="text-xs text-muted-foreground"
                    title={s.extraction && s.extraction.status !== 'ok' ? s.extraction.reason ?? undefined : undefined}
                  >
                    {/* SA-469 (spec §3): the kind in words; a non-ok extraction says so.
                        SA-1029: only the birth is the original; a later one is the customer's. */}
                    {i === birthSourceIndex(data.born_with_source) ? sourceKindWord(s.kind) : laterSourceKindWord(s.kind)} · {new Date(s.pasted_at).toLocaleString()}
                    {/* SA-401 finding 1: `pasted_by` is a user id — never rendered; only the resolved name. */}
                    {s.pasted_by_name ? ` · by ${s.pasted_by_name}` : ''}
                    {s.fields_set.length ? ` · set ${s.fields_set.join(', ')}` : ' · set nothing'}
                    {s.extraction && s.extraction.status !== 'ok' ? ` · extraction ${s.extraction.status}` : ''}
                  </p>
                  {s.text && <pre className="mt-1 whitespace-pre-wrap text-xs">{s.text}</pre>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
      {data && editing && (
        <form className="space-y-4 text-sm" onSubmit={(e) => { e.preventDefault(); void handleSave() }}>
          {/* SA-1044: an older server sends no map: no notice rather than a wrong one. */}
          {running && data.process.revision_steps !== undefined && changedIds.length > 0 && (
            <p className="rounded-lg border border-border bg-muted/40 p-2 text-xs">
              {revisionNotice('Saving', data.revision_count + 1,
                revisionLanding(data.process.revision_steps, changedIds), data.process.current_step ?? null)}
            </p>
          )}
          {!applying && (
            <section className="space-y-2">
              <label htmlFor="intake-paste" className="font-medium">Paste text</label>
              <Textarea id="intake-paste" rows={4} value={pasteText} onChange={(e) => setPasteText(e.target.value)} />
              <div className="flex items-center gap-3">
                <Button type="button" size="sm" variant="outline" disabled={extracting || !pasteText.trim()} onClick={handleExtract}>{extracting ? 'Extracting…' : 'Extract'}</Button>
                {acceptedLine && <span className="text-xs text-muted-foreground">{acceptedLine}</span>}
              </div>
              {extractError && <p className="text-destructive">{extractError}</p>}
            </section>
          )}
          {applying && acceptedLine && <p className="text-xs text-muted-foreground">{acceptedLine}</p>}
          {proposals && proposals.length > 0 && (
            <div className="space-y-2">
              <table aria-label="Proposals" className="w-full text-xs">
                <thead><tr><th className="text-left">Field</th><th className="text-left">Current</th><th className="text-left">Proposed</th><th>Take</th></tr></thead>
                <tbody>
                  {proposals.map((p) => (
                    <tr key={p.field}>
                      <td>{p.label}</td>
                      <td>{filled(p.current) ? words(p.current) : '—'}</td>
                      <td>{words(p.proposed)}</td>
                      <td className="text-center">
                        <input type="checkbox" aria-label={`take ${p.label}`} checked={p.checked}
                          onChange={(e) => setProposals((rows) => rows!.map((r) => (r.field === p.field ? { ...r, checked: e.target.checked } : r)))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Button type="button" size="sm" onClick={handleAccept}>Accept</Button>
            </div>
          )}
          <div className="space-y-3">
            {data.fields.map((f) => (
              <FieldBlock key={f.id} idPrefix="intake" field={asBlockField(f)} value={values[f.id]}
                onChange={(v) => setField(f.id, v)} onRestore={(v) => restoreField(f, v)}
                prefill={isBlank(loaded[f.id]) ? null : { value: loaded[f.id] }}
                source={f.provenance === 'extracted' ? extracted(f.id) : null} />
            ))}
          </div>
          {running && <Input id="intake-reason" label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="optional — why the request changed" maxLength={REASON_MAX} />}
          {saveError && <p className="text-destructive">{saveError}</p>}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => {
              setEditing(false); setApplying(null)
              if (deferredReload.current) { deferredReload.current = false; void load() }
            }}>Cancel</Button>
          </div>
        </form>
      )}
    </>
  )

  if (chrome === 'section') {
    return (
      <RecordSection id="intake" title="Intake" summary={intakeSummary(data, loadError, waiting.length)} defaultOpen={defaultOpen} focused={focused}>
        {body}
      </RecordSection>
    )
  }

  return (
    <CollapsibleSection title="Intake" subtitle={data ? `${data.fields.filter((f) => filled(f.value)).length}/${data.fields.length} fields` : undefined}
      storageKey="order-intake" variant="soft" icon="📋">
      {body}
    </CollapsibleSection>
  )
}
